import { useEffect, useRef } from 'react';
import { BetItem } from '../components/BettingSlipDrawer';
import { liveStreamManager, preStreamManager } from '../services/CatalogStreamManager';
import { EventSnapshot } from '../api/types';

const formatOdds = (odds: string): string => {
  return parseFloat(odds).toFixed(2).replace('.', ',');
};

export function useBetSlipOdds(
  bets: BetItem[],
  setBets: React.Dispatch<React.SetStateAction<BetItem[]>>
) {
  const betsRef = useRef(bets);
  betsRef.current = bets;

  useEffect(() => {
    if (bets.length === 0) return;

    const handleUpdates = (upserts: EventSnapshot[], deletes: string[]) => {
      let changed = false;
      const newBets = betsRef.current.map((bet) => {
        let newOdds = bet.odds;
        let newIsLocked = bet.isLocked || false;

        // 1. Handle deletes
        if (bet.eventId && deletes.includes(bet.eventId)) {
          if (!bet.isLocked) {
            changed = true;
            return { ...bet, isLocked: true };
          }
          return bet;
        }

        // 2. Handle upserts
        const msg = upserts.find(u => u.event_id === bet.eventId);
        if (!msg) return bet;

        const normalizedBetSpec = bet.specifiers || '';

        const checkMarkets = (marketsList: any[]) => {
          if (!marketsList || !Array.isArray(marketsList)) return;
          for (const m of marketsList) {
            let specStr = '';
            if (m.specifiers) {
              if (typeof m.specifiers === 'string') {
                specStr = m.specifiers;
              } else if (typeof m.specifiers === 'object') {
                specStr = Object.entries(m.specifiers).map(([k, v]) => `${k}=${v}`).join('|');
              }
            }

            if (
              (m.market_id === bet.marketId || m.canonical_key === bet.marketId) &&
              specStr === normalizedBetSpec
            ) {
              const mLocked = m.status === 0 || m.status === -1;

              if (m.outcomes && Array.isArray(m.outcomes)) {
                for (const o of m.outcomes) {
                  if (o.outcome_id === bet.outcomeId) {
                    const updatedOdds = formatOdds(String(o.odds));
                    const oLocked = !o.active || o.odds === 0 || o.odds === '0';

                    if (updatedOdds && updatedOdds !== 'NaN') {
                      newOdds = updatedOdds;
                    }
                    newIsLocked = mLocked || oLocked;
                  }
                }
              } else {
                if (mLocked) {
                  newIsLocked = true;
                } else if (m.status === 1) {
                  newIsLocked = false;
                }
              }
            }
          }
        };

        if (msg.markets) {
          checkMarkets(msg.markets);
        }

        if (newOdds !== bet.odds || newIsLocked !== (bet.isLocked || false)) {
          changed = true;
          return { ...bet, odds: newOdds, isLocked: newIsLocked };
        }

        return bet;
      });

      if (changed) {
        setBets(newBets);
      }
    };

    const unsubLive = liveStreamManager.subscribe(handleUpdates);
    const unsubPre = preStreamManager.subscribe(handleUpdates);

    return () => {
      unsubLive();
      unsubPre();
    };
  }, [bets.length > 0]);
}


